import React from 'react';
import { 
  ShieldCheckIcon, 
  KeyIcon, 
  EnvelopeIcon, 
  DocumentCheckIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  FingerPrintIcon,
  UserMinusIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

// High-density KPI Card with Progress Ring
const KPICard = ({ label, value, percentage, colorClass, icon: Icon }) => (
  <div className="bg-white border border-slate-200 p-3 rounded shadow-sm flex items-center justify-between hover:shadow-md transition-shadow cursor-default">
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <h3 className="text-xl font-bold text-slate-900 font-poppins">{value}</h3>
    </div>
    <div className="relative flex items-center justify-center">
      <svg className="w-10 h-10 transform -rotate-90">
        <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="3" fill="transparent" className="text-slate-100" />
        <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="3" fill="transparent" 
                strokeDasharray={100} strokeDashoffset={100 - percentage}
                className={colorClass} />
      </svg>
      <Icon className={`w-4 h-4 absolute ${colorClass}`} />
    </div>
  </div>
);

const AdminOverview = () => {
  
  const handleRefresh = () => console.log("Refreshing system health...");
  const handleExport = () => console.log("Exporting admin logs...");

  const adminLogs = [
    { name: 'Amara Walker', action: 'Modified Payroll Config', module: 'Finance', time: '2 mins ago', status: 'Success' },
    { name: 'David Chen', action: 'Bulk User Deletion', module: 'Directory', time: '14 mins ago', status: 'Warning' },
    { name: 'System Bot', action: 'Monthly Policy Sync', module: 'Governance', time: '1 hr ago', status: 'Success' },
    { name: 'Sarah Jenkins', action: 'API Key Rotation', module: 'Security', time: '3 hrs ago', status: 'Attention' },
    { name: 'Robert Fox', action: 'SSO Certificate Update', module: 'Auth', time: '5 hrs ago', status: 'Success' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-5 font-roboto text-slate-800">
      
      {/* 1. HEADER */}
      <header className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-poppins text-2xl font-bold text-slate-900">Admin Overview</h1>
          <p className="text-xs text-slate-500 font-medium">System health, admin activity & security status</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-slate-300 rounded bg-white hover:bg-slate-50 transition-all active:scale-95"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
          </button>
          <button 
            onClick={handleExport}
            className="px-3 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-all shadow-sm active:scale-95"
          >
            Export Logs
          </button>
        </div>
      </header>

      {/* 2. KPI METRICS (TOP ROW) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard label="Total Employees" value="1,284" percentage={85} colorClass="text-blue-500" icon={UserGroupIcon} />
        <KPICard label="Active Employees" value="1,120" percentage={92} colorClass="text-emerald-500" icon={ShieldCheckIcon} />
        <KPICard label="Pending Onboarding" value="48" percentage={15} colorClass="text-amber-500" icon={DocumentCheckIcon} />
        <KPICard label="Admin Users" value="12" percentage={40} colorClass="text-purple-500" icon={KeyIcon} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <div className="lg:col-span-2 space-y-6">
          {/* 3. SYSTEM HEALTH PANEL */}
          <section className="bg-white border border-slate-200 rounded shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <h2 className="font-poppins text-sm font-bold text-slate-700 uppercase tracking-tight">System Health Status</h2>
              <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> All Systems Operational
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
              {[
                { label: 'Auth Status', status: 'Healthy', color: 'text-emerald-600', icon: FingerPrintIcon },
                { label: 'SSO Status', status: 'Healthy', color: 'text-emerald-600', icon: KeyIcon },
                { label: 'Email Delivery', status: 'Warning', color: 'text-amber-600', icon: EnvelopeIcon },
                { label: 'Policy Sync', status: 'Attention', color: 'text-rose-600', icon: ArrowPathIcon },
              ].map((item, idx) => (
                <div key={idx} className="p-4 flex flex-col items-center hover:bg-slate-50 transition-colors group cursor-help">
                  <item.icon className="w-5 h-5 text-slate-400 mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">{item.label}</p>
                  <span className={`text-[11px] font-bold ${item.color}`}>{item.status}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 4. ADMIN ACTIVITY LOG (TABLE) */}
          <section className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <h2 className="font-poppins text-sm font-bold text-slate-700 uppercase tracking-tight">Recent Admin Activity</h2>
            </div>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-white sticky top-0 z-10">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase">Admin Name</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase">Action</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase">Module</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase">Timestamp</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {adminLogs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-4 py-2.5 text-xs font-bold text-slate-700">{log.name}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">{log.action}</td>
                      <td className="px-4 py-2.5 text-xs font-medium text-indigo-600 bg-indigo-50/30">{log.module}</td>
                      <td className="px-4 py-2.5 text-[11px] text-slate-400">{log.time}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                          log.status === 'Success' ? 'bg-green-100 text-green-700' : 
                          log.status === 'Warning' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* 5. SECURITY SNAPSHOT */}
        <aside className="space-y-4">
          <section className="bg-white border border-slate-200 rounded shadow-sm p-4">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
              <h2 className="font-poppins text-sm font-bold text-slate-700 uppercase tracking-tight">Security Snapshot</h2>
            </div>
            
            <div className="space-y-3">
              {[
                { label: 'MFA Enabled Users', value: '1,240', icon: FingerPrintIcon, color: 'text-emerald-600', sub: '98% Coverage' },
                { label: 'Pending Reviews', value: '04', icon: ExclamationTriangleIcon, color: 'text-amber-600', sub: 'Due in 2 days' },
                { label: 'Dormant Accounts', value: '18', icon: UserMinusIcon, color: 'text-slate-500', sub: 'Inactive > 90 days' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2.5 border border-slate-100 rounded hover:border-indigo-200 transition-colors group cursor-default">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded bg-slate-50 group-hover:bg-white transition-colors`}>
                      <item.icon className={`w-4 h-4 ${item.color}`} />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-slate-700">{item.label}</p>
                      <p className="text-[10px] text-slate-400">{item.sub}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-900 font-poppins">{item.value}</span>
                </div>
              ))}
            </div>

            <button 
              onClick={() => console.log("Navigating to Security Settings...")}
              className="w-full mt-4 py-2 text-[11px] font-bold text-indigo-600 border border-indigo-100 bg-indigo-50/30 rounded hover:bg-indigo-50 transition-colors"
            >
              Security Deep-Dive
            </button>
          </section>

          <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded p-4 text-white shadow-lg">
            <h4 className="text-[11px] font-bold uppercase tracking-widest opacity-80 mb-1">Compliance Score</h4>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-3xl font-bold font-poppins">94%</span>
              <span className="text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded mb-1">+2.4%</span>
            </div>
            <div className="w-full bg-white/20 h-1.5 rounded-full overflow-hidden">
              <div className="bg-white h-full transition-all duration-1000" style={{ width: '94%' }} />
            </div>
            <p className="text-[10px] mt-3 opacity-80 leading-relaxed italic">
              "Your system is compliant with ISO 27001 standards as of last audit."
            </p>
          </div>
        </aside>

      </div>
    </div>
  );
};

export default AdminOverview;